import { Col, Row } from 'react-bootstrap';
import PageContainer from '../../../components/container/PageContainer';
import BasePulldown from '../../../components/pulldown/BasePulldown';
import { useEffect, useRef, useState } from 'react';
import AddButton from '../../../components/button/AddButton';
import MultiSaveButton from '../../../components/button/MultiSaveButton';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createUnitPriceYear,
  exportUnitPricesCsv,
  getUnitPrices,
  getUnitPriceYears,
  updateUnitPrices,
} from '../../../services/api';
import { EmptyUnitPriceYearsGetResponse } from '../../../types/unitprice/UnitPriceYearsGerResponse';
import CsvExportButton from '../../../components/button/CsvExportButton';
import CsvModal from '../../../components/modal/CsvModal';
import YearPicker from '../../../components/picker/YearPicker';
import { add, format, parse, set } from 'date-fns';
import {
  EmptyUnitPriceGetResponse,
  UnitPriceEmployeeArray,
} from '../../../types/unitprice/UnitPriceGetResponse';
import MultiInputTable from '../../../components/table/MultiInputTable';
import { UNIT_PRICE_HEADER } from '../../../constants/unitPrice';
import {
  FormattedUnitPricePutRequest,
  UnitPricePutRequest,
} from '../../../types/unitprice/UnitPricePutRequest';
import UpdateModalOk from '../../../components/modal/UpdateModalOk';
import LoadingOverlay from '../../../components/LoadingOverlay';
import YmPicker from '../../../components/picker/YmPicker';
import {
  EmptyUnitPriceCsvExportParams,
  UnitPriceCsvExportParams,
} from '../../../types/unitprice/UnitPriceCsvExportParams';
import { saveBlobAsFile } from '../../../utils/saveBlobAsFile';
import useValidation from '../../../hooks/useValidation';
import { UNIT_PRICE_VALIDATION } from '../../../constants/validation/unitPriceValidation';
import SyncedScrollHeader from '../../../components/layout/SyncedScrollHeader';
import '../../../styles/UnitPriceHeader.css';
import {
  EmptyUnitPriceGetQueryParams,
  UnitPriceGetQueryParams,
} from '../../../types/unitprice/UnitPriceGetQueryParams';
import TokyoCheckbox from '../../../components/search/TokyoCheckbox';
import DairenCheckbox from '../../../components/search/DairenCheckbox';

const UnitPrice = () => {
  const queryClient = useQueryClient();
  const [showPeriodModal, setShowPeriodModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(0);
  const [yearToAdd, setYearToAdd] = useState<Date | null>(null);
  const [editingData, setEditingData] = useState<UnitPricePutRequest>(
    EmptyUnitPriceGetResponse
  );
  const [queryParams, setQueryParams] = useState<UnitPriceGetQueryParams>(
    EmptyUnitPriceGetQueryParams
  );
  const [csvExportParams, setCsvExportParams] =
    useState<UnitPriceCsvExportParams>(EmptyUnitPriceCsvExportParams);
  const { validate } = useValidation(UNIT_PRICE_VALIDATION);

  // 表の横スクロールを持つコンテナ参照（ヘッダーを幅＆scrollLeftに同期）
  const tableRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null); // 高さ計算用
  const footerRef = useRef<HTMLDivElement>(null); // 高さ計算用

  // 年プルダウンを取得
  const {
    data: yearOptions,
    isLoading: isYearOptionsLoading,
    error: yearOptionsError,
    isError: isYearOptionsError,
  } = useQuery({
    queryKey: ['unitPriceYearOptions'],
    queryFn: () => getUnitPriceYears(),
    initialData: EmptyUnitPriceYearsGetResponse,
  });

  // 指定した期間の個別単価を取得
  const {
    data: unitPrices,
    isLoading: isUnitPricesLoading,
    error: unitPricesError,
    isError: isUnitPricesError,
  } = useQuery({
    queryKey: ['unitPrices', selectedYear, queryParams],
    queryFn: () => getUnitPrices(selectedYear, queryParams),
    initialData: EmptyUnitPriceGetResponse,
    enabled: selectedYear !== 0,
  });

  // 取得した個別単価を編集用データにセット
  useEffect(() => {
    if (unitPrices) {
      const putData: UnitPricePutRequest = {
        employees: unitPrices.employees.map((emp) => ({
          unitPriceId: emp.unitPriceId,
          employeeId: emp.employeeId,
          employeeName: emp.employeeName,
          employeeCode: emp.employeeCode,
          unitPrice: { ...emp.unitPrice },
        })),
      };
      setEditingData(putData);
    }
  }, [unitPrices]);

  // 個別単価更新
  const editRequest = useMutation({
    mutationFn: (data: FormattedUnitPricePutRequest) =>
      updateUnitPrices(selectedYear, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unitPrices', selectedYear] });
    },
    onError: (error: any) => alert(error.response.data.message),
  });

  // CSV出力
  const { mutateAsync: exportCsv } = useMutation({
    mutationKey: ['exportUnitPricesCsv'],
    mutationFn: () => exportUnitPricesCsv(csvExportParams),
  });

  const handleCsvModalOkButtonClick = async () => {
    try {
      const { blob, filename } = await exportCsv();
      const fallback = 'unitprices.csv';
      const resolved =
        filename && filename.trim() ? decodeURIComponent(filename) : fallback;

      const ok = await saveBlobAsFile(blob, resolved);
      if (ok) alert('CSVファイルの保存が完了しました');
    } catch (err: any) {
      let msg = '';
      const data = err?.response?.data;
      if (data instanceof Blob) {
        const text = await data.text();
        const json = JSON.parse(text);
        if (json?.message) msg = json.message;
      }
      alert(msg);
    }
    setShowCsvModal(false);
  };

  const handlePeriodModalOkButtonClick = () => {
    if (yearToAdd === null) {
      alert('期間を選択してください');
      return;
    }
    addUnitPriceYear.mutate(format(yearToAdd, 'yyyy'));
    setShowPeriodModal(false);
  };

  // 期間を追加
  const addUnitPriceYear = useMutation({
    mutationFn: (year: string) => createUnitPriceYear(year),
    onSuccess: () => {
      setYearToAdd(null);
      queryClient.invalidateQueries({ queryKey: ['unitPriceYearOptions'] });
    },
    onError: (error: any) => alert(error.response.data.message),
  });

  const handleInputChange = (id: number, field: string, value: string) => {
    setEditingData((prev) => {
      const updatedEmployees = prev.employees.map((emp) => {
        if (emp.unitPriceId === id) {
          return {
            ...emp,
            unitPrice: {
              ...emp.unitPrice,
              [field]: value === '' ? null : value,
            },
          };
        }
        return emp;
      });
      return { ...prev, employees: updatedEmployees };
    });
  };

  const handleAllSaveButtonClick = () => {
    const formattedData: FormattedUnitPricePutRequest = {
      employees: editingData.employees.map((emp) => {
        const { average, ...unitPriceWithoutAverage } = emp.unitPrice;
        return {
          unitPriceId: emp.unitPriceId,
          unitPrice: unitPriceWithoutAverage,
        };
      }),
    };
    for (const emp of formattedData.employees) {
      const errorMessage = validate(emp.unitPrice);
      if (errorMessage) {
        alert(errorMessage);
        return;
      }
    }
    editRequest.mutate(formattedData);
  };

  const isMutating =
    addUnitPriceYear.status === 'pending' || editRequest.status === 'pending'
      ? true
      : false;

  return (
    <PageContainer title="個別単価一覧">
      <LoadingOverlay
        isLoading={isYearOptionsLoading || isUnitPricesLoading}
        isMutating={!!isMutating}
      />
      <SyncedScrollHeader
        targetRef={tableRef}
        className="mb-3"
        outerRef={headerRef}
      >
        <Row className="header-row-unitprice gx-0">
          {/* 左：検索条件（縮めない） */}
          <Col className="header-fields-unitprice order-1">
            <div className="header-left-unitprice">
              <BasePulldown
                itemList={yearOptions}
                selectedItem={selectedYear}
                defaultLabel="期間を選択"
                onChange={(id) => setSelectedYear(id)}
              />
              <TokyoCheckbox
                checked={queryParams.isTokyo}
                onChange={(checked) =>
                  setQueryParams((prev) => ({ ...prev, isTokyo: checked }))
                }
                className="ms-3"
              />
              <DairenCheckbox
                checked={queryParams.isDairen}
                onChange={(checked) =>
                  setQueryParams((prev) => ({ ...prev, isDairen: checked }))
                }
                className="ms-3"
              />
            </div>
          </Col>

          {/* 右：ボタン群（検索/リセットは無し。追加/保存/CSVは折り返し対象） */}
          <Col xs="auto" className="header-right-unitprice order-2">
            <div className="actions-group-unitprice">
              <div className="actions-searchreset-unitprice">
                <AddButton
                  buttonLabel="期間"
                  onClick={() => setShowPeriodModal(true)}
                />
                <MultiSaveButton onClick={() => setShowConfirmModal(true)} />
              </div>
              <div className="actions-extra-unitprice">
                <CsvExportButton onClick={() => setShowCsvModal(true)} />
              </div>
            </div>
          </Col>
        </Row>
      </SyncedScrollHeader>

      <MultiInputTable<UnitPriceEmployeeArray>
        headers={UNIT_PRICE_HEADER}
        items={selectedYear !== 0 ? editingData.employees : []}
        keyField="unitPriceId"
        rowTitleField="employeeCode"
        rowTitleSubfield="employeeName"
        dataField="unitPrice"
        textField={['average']}
        onChange={handleInputChange}
        scrollContainerRef={tableRef}
        headerRef={headerRef}
        footerRef={footerRef}
      />

      {showPeriodModal && (
        <CsvModal
          show={showPeriodModal}
          onClose={() => setShowPeriodModal(false)}
          onConfirm={() => handlePeriodModalOkButtonClick()}
          modalTitle="追加する期間を選択してください"
          saveButtonLabel="OK"
        >
          <div className="justify-content-center d-flex mt-5 mb-5">
            <YearPicker selectedYear={yearToAdd} onYearChange={setYearToAdd} />
          </div>
        </CsvModal>
      )}
      {showConfirmModal && (
        <UpdateModalOk
          show={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onSave={() => {
            handleAllSaveButtonClick();
            setShowConfirmModal(false);
          }}
        />
      )}
      {showCsvModal && (
        <CsvModal
          show={showCsvModal}
          onClose={() => setShowCsvModal(false)}
          onConfirm={async () => {
            await handleCsvModalOkButtonClick();
            setShowCsvModal(false);
          }}
          saveButtonLabel="OK"
        >
          <div className="d-flex flex-column align-items-center justify-content-center mt-4">
            <div className="d-flex justify-content-center align-items-center mb-4">
              <YmPicker
                placeholder="期間を選択"
                selectedMonth={
                  csvExportParams.startDate
                    ? parse(csvExportParams.startDate, 'yyyy-MM', new Date())
                    : null
                }
                setSelectedMonth={(date) =>
                  setCsvExportParams((prev) => ({
                    ...prev,
                    startDate: date ? format(date, 'yyyy-MM') : '',
                  }))
                }
              />
              <p className="mx-2 mb-0">～</p>
              <YmPicker
                placeholder="期間を選択"
                selectedMonth={
                  csvExportParams.endDate
                    ? parse(csvExportParams.endDate, 'yyyy-MM', new Date())
                    : null
                }
                setSelectedMonth={(date) =>
                  setCsvExportParams((prev) => ({
                    ...prev,
                    endDate: date ? format(date, 'yyyy-MM') : '',
                  }))
                }
              />
            </div>
            <div className="d-flex justify-content-center align-items-center mb-4">
              <TokyoCheckbox
                checked={csvExportParams.isTokyo}
                onChange={(checked) =>
                  setCsvExportParams((prev) => ({
                    ...prev,
                    isTokyo: checked,
                  }))
                }
                className="ms-3"
              />
              <DairenCheckbox
                checked={csvExportParams.isDairen}
                onChange={(checked) =>
                  setCsvExportParams((prev) => ({
                    ...prev,
                    isDairen: checked,
                  }))
                }
                className="ms-3"
              />
            </div>
          </div>
        </CsvModal>
      )}
    </PageContainer>
  );
};

export default UnitPrice;
